import React, { useState } from 'react';
import { Save, Loader2, Lock } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, DocumentosPessoais } from '../../shared/atlasApi';
import { inputCls, labelCls } from '../../shared/ListaEditavel';
import FeedbackBanner, { Feedback } from '../../shared/FeedbackBanner';

interface Props {
  user: AtlasUser;
  colaborador: Colaborador;
  podeEditar: boolean;
  onSalvo: () => void;
}

const AbaDocumentos: React.FC<Props> = ({ user, colaborador, podeEditar, onSalvo }) => {
  const dp = colaborador.documentos_pessoais || {};
  const ctps0 = dp.ctps || {};
  const cnh0 = dp.cnh || {};

  const [docs, setDocs] = useState<DocumentosPessoais & { ctps: NonNullable<DocumentosPessoais['ctps']>; cnh: NonNullable<DocumentosPessoais['cnh']> }>({
    cpf: dp.cpf || colaborador.cpf || '',
    rg: dp.rg || colaborador.rg || '',
    pis: dp.pis || '',
    titulo_eleitor: dp.titulo_eleitor || '',
    ctps: { numero: ctps0.numero || '', serie: ctps0.serie || '', uf: ctps0.uf || '' },
    cnh: { numero: cnh0.numero || '', categoria: cnh0.categoria || '', validade: cnh0.validade || '' },
    reservista: dp.reservista || '',
  });
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const set = (patch: Partial<typeof docs>) => setDocs(p => ({ ...p, ...patch }));
  const setCtps = (patch: Partial<typeof docs.ctps>) => setDocs(p => ({ ...p, ctps: { ...p.ctps, ...patch } }));
  const setCnh = (patch: Partial<typeof docs.cnh>) => setDocs(p => ({ ...p, cnh: { ...p.cnh, ...patch } }));

  const salvar = async () => {
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.putDocumentosPessoais(user, colaborador.funccodi, {
        cpf: docs.cpf,
        rg: docs.rg,
        pis: docs.pis,
        titulo_eleitor: docs.titulo_eleitor,
        ctps: docs.ctps,
        cnh: docs.cnh,
        reservista: docs.reservista,
      });
      setFeedback({ tipo: 'ok', msg: 'Documentos salvos com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const ro = !podeEditar;

  return (
    <div className="space-y-4">
      <FeedbackBanner feedback={feedback} />

      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
        <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
        Estes dados são criptografados antes de gravar e o acesso é auditado (LGPD).
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>CPF</label>
          <input className={inputCls} disabled={ro} value={docs.cpf} onChange={ev => set({ cpf: ev.target.value })} />
        </div>
        <div>
          <label className={labelCls}>RG</label>
          <input className={inputCls} disabled={ro} value={docs.rg} onChange={ev => set({ rg: ev.target.value })} />
        </div>
        <div>
          <label className={labelCls}>PIS / PASEP</label>
          <input className={inputCls} disabled={ro} value={docs.pis} onChange={ev => set({ pis: ev.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Título de eleitor</label>
          <input className={inputCls} disabled={ro} value={docs.titulo_eleitor} onChange={ev => set({ titulo_eleitor: ev.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Reservista</label>
          <input className={inputCls} disabled={ro} value={docs.reservista} onChange={ev => set({ reservista: ev.target.value })} />
        </div>
      </div>

      {/* CTPS */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-purple-700 uppercase tracking-wider">CTPS</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Número</label>
            <input className={inputCls} disabled={ro} value={docs.ctps.numero} onChange={ev => setCtps({ numero: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Série</label>
            <input className={inputCls} disabled={ro} value={docs.ctps.serie} onChange={ev => setCtps({ serie: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>UF</label>
            <input className={inputCls} disabled={ro} maxLength={2} value={docs.ctps.uf} onChange={ev => setCtps({ uf: ev.target.value.toUpperCase().slice(0, 2) })} />
          </div>
        </div>
      </div>

      {/* CNH */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-purple-700 uppercase tracking-wider">CNH</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Número</label>
            <input className={inputCls} disabled={ro} value={docs.cnh.numero} onChange={ev => setCnh({ numero: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Categoria</label>
            <input className={inputCls} disabled={ro} value={docs.cnh.categoria} onChange={ev => setCnh({ categoria: ev.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Validade</label>
            <input type="date" className={inputCls} disabled={ro} value={(docs.cnh.validade || '').slice(0, 10)} onChange={ev => setCnh({ validade: ev.target.value })} />
          </div>
        </div>
      </div>

      {podeEditar && (
        <div className="flex justify-end pt-1">
          <button
            onClick={salvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar documentos
          </button>
        </div>
      )}
    </div>
  );
};

export default AbaDocumentos;
